// frontend/src/components/ui/SavingOverlay.js
// 保存中の全画面オーバーレイ。createPortal で document.body に直接マウントし、
// 背後の操作を完全にブロックする。スマホで「保存ボタン押下後にすぐ次の操作」を防ぐ用途。
//
// 使い方:
//   const [saving, setSaving] = useState(false);
//   <SavingOverlay visible={saving} message="経験録を保存中..." />
//
// visible が true の間:
//   - 半透明の黒オーバーレイで背景を覆う
//   - 中央にスピナー + メッセージ表示
//   - z-index 最大、pointer-events 全捕捉でクリック/タッチを阻止
//   - 背景スクロールも一時ロック

import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Loader2 } from 'lucide-react';

const SavingOverlay = ({
  visible,
  message = '保存中...',
  subMessage = '画面操作はそのままお待ちください',
}) => {
  // 背景スクロールロック（visible の間だけ）
  useEffect(() => {
    if (!visible) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [visible]);

  if (!visible) return null;

  return createPortal(
    <div
      role="status"
      aria-live="polite"
      aria-busy="true"
      onClick={(e) => e.stopPropagation()}
      onTouchStart={(e) => e.stopPropagation()}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15, 23, 42, 0.55)',
        backdropFilter: 'blur(2px)',
        WebkitBackdropFilter: 'blur(2px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2147483646, // 事実上の最大値（モーダル類より前面）
        pointerEvents: 'all',
        touchAction: 'none',
        padding: '1rem',
      }}
    >
      <div
        style={{
          background: 'white',
          borderRadius: 16,
          padding: '1.5rem 2rem',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          minWidth: 220,
          maxWidth: '90vw',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 12,
        }}
      >
        <Loader2
          size={40}
          color="#0891b2"
          style={{ animation: 'spin 1s linear infinite' }}
        />
        <div style={{ fontSize: '1rem', fontWeight: 700, color: '#0f172a', textAlign: 'center' }}>
          {message}
        </div>
        {subMessage && (
          <div style={{ fontSize: '0.8rem', color: '#64748b', textAlign: 'center' }}>
            {subMessage}
          </div>
        )}
      </div>
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>,
    document.body
  );
};

export default SavingOverlay;
